import React, { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { Feature } from '@guest-widgets/shared/src/features';
import { productApi } from '@guest-widgets/shared/src/apis/guestExperience';
import { usePrevious } from '@guest-widgets/shared/src/hooks/usePrevious';
import type {
  CompanyDto,
  ListProductDto,
} from '@checkfront/guest-experience-api-api-client-javascript-axios';
import { useFeature } from '@guest-widgets/shared/src/contexts/featureContext/featureContext';
import { useInfiniteQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { useSettings } from './settingsContext';
import type { Filters, CategoryCode } from './filters';
import { useFilters } from './filtersContext/filtersContext';
import { useFilterAttributes } from './filterAttributesContext';

const PAGE_SIZE = 12;

export interface ProductsContext {
  products: ListProductDto[];
  totalCount?: number;
  loadProducts: () => void;
  hasMorePages: boolean;
  companyInformation?: CompanyDto;
  productsPerPage?: ListProductDto[];
  isLoading: boolean;
  pageSize: number;
  isSuccess?: boolean;
  isError?: boolean;
}

export const defaultProductsContextValue: ProductsContext = {
  products: [],
  totalCount: undefined,
  loadProducts: () => {},
  hasMorePages: false,
  companyInformation: undefined,
  productsPerPage: [],
  isLoading: false,
  pageSize: PAGE_SIZE,
  isSuccess: false,
  isError: false,
};

const productsContext = createContext<ProductsContext | undefined>(undefined);

export const ProductsProvider = ({ children }: PropsWithChildren<{}>) => {
  const { isEnabled } = useFeature();
  const { customerCode, locale } = useSettings();
  const { filters } = useFilters();
  const { attributes } = useFilterAttributes();
  const [companyInformation, setCompanyInformation] = useState<CompanyDto>();

  const includeInactive = isEnabled(Feature.ShowInactiveProducts);
  const categoryCode = attributes?.categoryCode;

  const fetchProducts = async ({ pageParam = 0 }) => {
    const requestParams = mapToRequestParams(filters, categoryCode, includeInactive);
    const { items, totalItemCount, company } = await productApi.getProducts(customerCode, locale, {
      ...requestParams,
      offset: pageParam,
      limit: PAGE_SIZE,
    });

    setCompanyInformation(company);
    return { items, totalItemCount, nextCursor: pageParam + PAGE_SIZE };
  };

  const requestParams = useMemo(
    () => mapToRequestParams(filters, categoryCode, includeInactive),
    [filters, categoryCode, includeInactive]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isSuccess, isError } =
    useInfiniteQuery({
      queryKey: ['products', customerCode, locale, requestParams],
      queryFn: fetchProducts,
      getNextPageParam: (lastPage) =>
        lastPage.items.length < PAGE_SIZE ? undefined : lastPage.nextCursor,
    });

  const products = data?.pages.flatMap((page) => page.items) || [];
  const totalCount = data?.pages[0]?.totalItemCount;
  const productsPerPage = data?.pages?.[data.pages.length - 1]?.items || [];

  const value: ProductsContext = {
    products,
    totalCount,
    loadProducts: () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    hasMorePages: !!hasNextPage,
    companyInformation,
    productsPerPage,
    isLoading,
    pageSize: PAGE_SIZE,
    isSuccess,
    isError,
  };

  return <productsContext.Provider value={value}>{children}</productsContext.Provider>;
};

export const useProducts = () => {
  const context = useContext(productsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

// Helper function to map filters to API request params
const mapToRequestParams = (
  filters: Filters,
  categoryCode?: CategoryCode,
  includeInactive?: boolean
) => {
  let params: Record<string, any> = { includeInactive };

  if (filters.dates) {
    const [from, to] = filters.dates;
    params.dateFrom = dayjs(from).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
    params.dateTo = dayjs(to ?? from)
      .endOf('day')
      .format('YYYY-MM-DDTHH:mm:ss');
  }

  if (filters.location) {
    params.latitude = filters.location.latitude;
    params.longitude = filters.location.longitude;
    params.radius = filters.location.radius * 1000;
  }

  if (filters.price) {
    const [min, max] = filters.price;
    params.minPrice = min.toString();
    params.maxPrice = max.toString();
  }

  if (filters.categories && categoryCode) {
    params[categoryCode] = filters.categories.join(',');
  }

  if (filters.languages) {
    params.languages = filters.languages.join(',');
  }

  if (filters.sort) {
    params.sort = filters.sort;
  }

  if (filters.misc) {
    params.topThingsToDo = filters.misc.join(',');
  }

  return params;
};
